import { html } from "htm/preact";
import type { JSONSchema } from "../../../shared/formats/types.js";

/**
 * Renders a format's setup fields from its own `configSchema` (spec §4: "What setup
 * UI to render for this format"). Keeping this generic means adding a format never
 * means editing the setup screen — it just declares its schema and gets real inputs
 * instead of a raw JSON box.
 *
 * Supports the shapes the formats actually use: numbers, booleans, string enums, and
 * one level of nested object (Wolf's per-outcome point values).
 */
type SchemaNode = {
  type?: string;
  title?: string;
  description?: string;
  default?: unknown;
  enum?: string[];
  properties?: Record<string, SchemaNode>;
};

function humanise(key: string): string {
  const spaced = key.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function Field({
  name,
  node,
  value,
  onChange,
}: {
  name: string;
  node: SchemaNode;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = node.title ?? humanise(name);
  const current = value ?? node.default;

  if (node.type === "boolean") {
    return html`
      <div class="field mt-3">
        <label class="row">
          <input
            type="checkbox"
            class="checkbox"
            checked=${Boolean(current)}
            onChange=${(e: any) => onChange(e.currentTarget.checked)}
          />
          <span>${label}</span>
        </label>
        ${node.description && html`<p class="field-help field-help-tight">${node.description}</p>`}
      </div>
    `;
  }

  if (node.enum) {
    return html`
      <label class="field mt-3">
        <span class="field-label">${label}</span>
        <select value=${String(current ?? "")} onChange=${(e: any) => onChange(e.currentTarget.value)}>
          ${node.enum.map((opt) => html`<option key=${opt} value=${opt}>${humanise(opt)}</option>`)}
        </select>
        ${node.description && html`<p class="field-help field-help-tight">${node.description}</p>`}
      </label>
    `;
  }

  if (node.type === "number") {
    return html`
      <label class="field mt-3">
        <span class="field-label">${label}</span>
        <input
          type="number"
          inputmode="decimal"
          step="any"
          value=${current === undefined || current === null ? "" : String(current)}
          onInput=${(e: any) => {
            const raw = e.currentTarget.value;
            if (raw === "") {
              onChange(undefined);
              return;
            }
            const n = Number(raw);
            // Ignore a transiently-invalid keystroke (e.g. a lone "-") rather than
            // writing NaN into the config, which would silently serialize to null.
            if (!Number.isFinite(n)) return;
            onChange(n);
          }}
        />
        ${node.description && html`<p class="field-help field-help-tight">${node.description}</p>`}
      </label>
    `;
  }

  return html`
    <label class="field mt-3">
      <span class="field-label">${label}</span>
      <input
        value=${current === undefined || current === null ? "" : String(current)}
        onInput=${(e: any) => onChange(e.currentTarget.value)}
      />
    </label>
  `;
}

export function FormatConfigForm({
  schema,
  value,
  onChange,
}: {
  schema: JSONSchema;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const props = (schema as SchemaNode).properties ?? {};
  const entries = Object.entries(props);
  if (entries.length === 0) return null;

  return html`
    <div class="mt-3">
      <span class="field-label">Format options</span>
      ${entries.map(([key, node]) => {
        if (node.type === "object" && node.properties) {
          const nested = (value[key] as Record<string, unknown>) ?? {};
          return html`
            <fieldset class="nested-config" key=${key}>
              <legend class="field-label">${node.title ?? humanise(key)}</legend>
              ${Object.entries(node.properties).map(
                ([nk, nn]) => html`
                  <${Field}
                    key=${nk}
                    name=${nk}
                    node=${nn}
                    value=${nested[nk]}
                    onChange=${(v: unknown) => onChange({ ...value, [key]: { ...nested, [nk]: v } })}
                  />
                `,
              )}
            </fieldset>
          `;
        }
        return html`
          <${Field}
            key=${key}
            name=${key}
            node=${node}
            value=${value[key]}
            onChange=${(v: unknown) => onChange({ ...value, [key]: v })}
          />
        `;
      })}
    </div>
  `;
}
